import { useState, useRef, useEffect } from "react";
import Nav from "@/components/Nav";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Terminal, ChevronRight } from "lucide-react";

const STEPS = ["identity", "scanning", "plan"];

export default function ScanWizard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", city: "", state: "" });
  const [scan, setScan] = useState(null);
  const [terminal, setTerminal] = useState([]);
  const [plan, setPlan] = useState("");
  const [planLoading, setPlanLoading] = useState(false);
  const termRef = useRef(null);

  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  useEffect(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, [terminal]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const runScan = async (e) => {
    e.preventDefault();
    setStep(1);
    setTerminal(["> initializing OBSIDIAN v1.4.2", "> auth: [OK] operative verified", "> launching deep-scan across 412 brokers..."]);

    // simulated typing
    const sleep = (ms) => new Promise(r => setTimeout(r, ms));
    const brokers = ["whitepages.com", "spokeo.com", "beenverified.com", "radaris.com", "acxiom.com", "mylife.com", "intelius.com", "peekyou.com"];
    for (let b of brokers) {
      await sleep(280);
      setTerminal(t => [...t, `[SCAN] ${b} …`]);
      await sleep(220);
      setTerminal(t => [...t, `[FOUND] ${b} :: match confirmed`]);
    }
    setTerminal(t => [...t, "> aggregating exposure metrics…"]);

    try {
      const { data } = await api.post("/scans", form);
      setScan(data);
      setTerminal(t => [...t, `> exposure_score: ${data.exposure_score}/99 [${data.risk_level.toUpperCase()}]`, "> handing off to AI wizard for removal plan…"]);
      await sleep(500);
      setStep(2);
      streamPlan(data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scan failed");
      setStep(0);
    }
  };

  const streamPlan = async (scanId) => {
    setPlan("");
    setPlanLoading(true);
    const token = localStorage.getItem("obsidian_token");
    try {
      const resp = await fetch(`${API}/ai/plan/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scan_id: scanId }),
      });
      if (!resp.ok) throw new Error("Plan stream failed");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (p.startsWith("data: ")) {
            const chunk = p.slice(6);
            if (chunk === "[DONE]") { setPlanLoading(false); return; }
            if (chunk.startsWith("[ERROR]")) { toast.error(chunk); setPlanLoading(false); return; }
            setPlan(prev => prev + chunk);
          }
        }
      }
    } catch (e) {
      toast.error("AI stream error");
    } finally { setPlanLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="grain min-h-screen bg-[#050505] text-white">
      <Nav />
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ SCAN WIZARD · STEP {step + 1} / 3</div>
        <h1 className="font-display text-4xl font-medium tracking-tighter md:text-5xl">
          {step === 0 && "Identify the target."}
          {step === 1 && "Scanning the dark…"}
          {step === 2 && "The plan."}
        </h1>

        {/* progress */}
        <div className="my-8 flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} data-testid={`step-indicator-${i}`}
              className={`h-1 flex-1 ${i <= step ? "bg-[#E5FF00]" : "bg-white/10"}`}></div>
          ))}
        </div>

        {step === 0 && (
          <form onSubmit={runScan} className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[
              { k: "full_name", label: "Full Name", required: true, colSpan: "md:col-span-2" },
              { k: "email", label: "Email", type: "email", required: true },
              { k: "phone", label: "Phone (optional)" },
              { k: "city", label: "City (optional)" },
              { k: "state", label: "State (optional)" },
            ].map(({ k, label, type = "text", required, colSpan = "" }) => (
              <div key={k} className={colSpan}>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{label}</Label>
                <Input data-testid={`scan-${k}`} type={type} required={required} value={form[k]} onChange={set(k)}
                  className="mt-2 rounded-none border-white/10 bg-black font-mono text-sm focus-visible:border-[#E5FF00] focus-visible:ring-0" />
              </div>
            ))}
            <div className="md:col-span-2 mt-2 flex items-center justify-between border-t border-white/10 pt-6">
              <p className="max-w-md font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Data is encrypted end-to-end. Only YOU can view your scan report.
              </p>
              <Button data-testid="scan-submit" type="submit"
                className="btn-cta h-12 rounded-none border border-[#E5FF00] bg-[#E5FF00] px-8 font-mono text-[11px] uppercase tracking-[0.25em] text-black hover:bg-transparent hover:text-[#E5FF00]">
                Initiate Deep Scan <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </form>
        )}

        {(step === 1 || step === 2) && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="relative overflow-hidden border border-white/10 bg-[#0A0A0C] scanline">
            <div className="flex items-center justify-between border-b border-white/10 bg-black/60 px-4 py-2">
              <div className="flex items-center gap-2">
                <Terminal className="h-3 w-3 text-[#E5FF00]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">obsidian@dark ~ scan.log</span>
              </div>
            </div>
            <div ref={termRef} className="max-h-[420px] overflow-y-auto p-5 font-mono text-[11px] leading-relaxed">
              {terminal.map((l, i) => (
                <div key={i} className={l.includes("[FOUND]") ? "text-[#FF3333]" : l.includes("[OK]") || l.includes("exposure_score") ? "text-[#E5FF00]" : "text-zinc-300"}>{l}</div>
              ))}
              {step === 1 && <div className="text-[#E5FF00] caret"></div>}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 border border-[#E5FF00]/40 bg-[#E5FF00]/[0.03] p-8">
            <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ AI-GENERATED REMOVAL PLAN · CLAUDE SONNET 4.5</div>
            <div data-testid="ai-plan" className="whitespace-pre-wrap font-mono text-sm leading-relaxed text-zinc-200">
              {plan}
              {planLoading && <span className="caret"></span>}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 border-t border-white/10 pt-6">
              <Button data-testid="btn-goto-dashboard" onClick={() => nav("/dashboard")}
                className="btn-cta h-11 rounded-none border border-[#E5FF00] bg-[#E5FF00] font-mono text-[11px] uppercase tracking-[0.25em] text-black hover:bg-transparent hover:text-[#E5FF00]">
                Open Threat Console →
              </Button>
              <Button data-testid="btn-goto-pricing" variant="outline" onClick={() => nav("/pricing")}
                className="btn-cta h-11 rounded-none border-white/20 bg-transparent font-mono text-[11px] uppercase tracking-[0.25em] hover:border-white hover:text-white">
                Automate Removals
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
